"use client"

import React from "react"
import { styled } from "@mui/material/styles"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"
import LaunchIcon from "@mui/icons-material/Launch"
import ClearIcon from "@mui/icons-material/Clear"
import { IconButton, Button, FormControl, FormHelperText } from "@mui/material"
import api from "../services/api"
import { useStoreActions } from "easy-peasy"
import { driveBase } from "../config"

const FileInput = styled("input")({
  display: "none",
})

const FileField = ({ field, useFormRef, schema, disabled, fileData, uploadUrl = "/upload", ...props }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormRef
  const startLoading = useStoreActions((actions) => actions.nav.startLoading)
  const stopLoading = useStoreActions((actions) => actions.nav.stopLoading)
  const [file, setFile] = React.useState(null)

  const onOpenFileClick = async () => {
    if (!file) return;
  
    const { mimeType, fileRef, fileName } = file;
    const API_URL_BASE = process.env.REACT_APP_API_URL_BASE;
  
    let fullUrl = fileRef.startsWith("http") ? fileRef : new URL(fileRef, API_URL_BASE).toString();
  
    // Tipos de arquivo que são abertos em nova aba
    const openInNewTabTypes = ["application/pdf", "text/html"];

    if (openInNewTabTypes.includes(mimeType)) {
      window.open(fullUrl, "_blank"); // Abre em nova aba
    } else {
      downloadFile(fullUrl, fileName);
    }
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
  
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao baixar o arquivo:", error);
    }
  };
  
  
  const clear = () => {
    setFile(null)
    setValue(field, null, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const onChange = async (event) => {
    if (event.target.files.length > 0) {
      const formData = new FormData()
      formData.append("file", event.target.files[0])
      startLoading()
      try {
        const uploaded = (
          await api.http.post(uploadUrl, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        ).data
        setFile(uploaded)
        setValue(field, uploaded.id, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        })
        stopLoading()
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: "error",
        })
      }
    }
  }

  React.useEffect(() => {
    async function loadFileData() {
      if (fileData) {
        let correctedFileRef = fileData.fileRef.replace(/\/uploads\/uploads\//g, '/uploads/')
        setFile({ ...fileData, fileRef: correctedFileRef })
      }
    }
    loadFileData()
  }, [fileData])

  const icons = (fileName) => {
    const isOpenable = file.mimeType === "application/pdf" || file.mimeType === "text/html";
  
    return (
      <a onClick={onOpenFileClick} style={{ cursor: "pointer" }}>
        <IconButton color="primary">
          {isOpenable ? <LaunchIcon /> : <CloudDownloadIcon />}
        </IconButton>
        {fileName}
      </a>
    );
  };

  return (
    <FormControl>
      <label htmlFor={field}>
        <FileInput id={field} type="file" onChange={onChange} disabled={disabled} />
        <Button
          color="primary"
          variant="outlined"
          aria-label="upload picture"
          component="span"
          disabled={disabled}
          endIcon={<CloudUploadIcon />}
        >
          {schema.fields[field].spec.label}
        </Button>
      </label>
      <input type="hidden" {...register(field)} />
      {file && (
        <div>
          {icons(file.fileName)}
          <IconButton onClick={clear} disabled={disabled}>
            <ClearIcon />
          </IconButton>
        </div>
      )}
      <FormHelperText error={errors[field] != null}>{errors[field]?.message}</FormHelperText>
    </FormControl>
  )
}

export default FileField

